import type { OpportunityFormatKey } from "@/types/opportunity";
import type { OpportunityDocumentArchetype } from "./documentArchetypes";

/** Built-in proposal page kinds (Path A manifest). */
export const PROPOSAL_BUILTIN_PAGE_KINDS = [
  "cover_letter",
  "cost_breakdown",
  "payment_schedule",
  "intent_to_purchase",
] as const;

export type ProposalBuiltinPageKind = (typeof PROPOSAL_BUILTIN_PAGE_KINDS)[number];

export const PROPOSAL_DOC_REF_PAGE_KIND = "doc_ref" as const;

export type ProposalPageKind = ProposalBuiltinPageKind | typeof PROPOSAL_DOC_REF_PAGE_KIND;

export const PROPOSAL_PAGE_KINDS = PROPOSAL_BUILTIN_PAGE_KINDS;

export const PROPOSAL_PAGE_KIND_LABELS: Record<ProposalBuiltinPageKind, string> = {
  cover_letter: "Cover letter",
  cost_breakdown: "Cost breakdown",
  payment_schedule: "Payment schedule",
  intent_to_purchase: "Intent / signature",
};

export const DEFAULT_PROPOSAL_ALTERNATE_KEY = "default";

export type ProposalAlternatePage = {
  page_kind: ProposalPageKind;
  sort_order: number;
  included: boolean;
  doc_document_id?: string | null;
  /** Library page supplying copy for builtin kinds (Phase 2). */
  proposal_page_id?: string | null;
};

export type ProposalManifestEntry =
  | { type: "builtin"; pageKind: ProposalBuiltinPageKind }
  | { type: "doc_ref"; docDocumentId: string; title: string };

export type ProposalAlternate = {
  id: string;
  organization_id: string;
  format_key: OpportunityFormatKey;
  product_type: string;
  alternate_key: string;
  label: string;
  description: string | null;
  sort_order: number;
  is_default: boolean;
  /** Rows predating the archetype column are proposals (server normalizes). */
  archetype: OpportunityDocumentArchetype;
  pages: ProposalAlternatePage[];
  created_at: string;
  updated_at: string;
};

export const DEFAULT_PROPOSAL_ALTERNATE_PAGES: ProposalAlternatePage[] =
  PROPOSAL_BUILTIN_PAGE_KINDS.map((page_kind, sort_order) => ({
    page_kind,
    sort_order,
    included: true,
  }));

/**
 * Standard contract manifest: the intent/signature built-in page only.
 * Real contract bodies come from Document Builder `doc_ref` pages on
 * manager-authored contract alternates.
 */
export const DEFAULT_CONTRACT_ALTERNATE_PAGES: ProposalAlternatePage[] = [
  { page_kind: "intent_to_purchase", sort_order: 0, included: true },
];

export function defaultProposalAlternate(args: {
  formatKey: OpportunityFormatKey;
  productType: string;
}): ProposalAlternate {
  return defaultAlternateForArchetype({ ...args, archetype: "proposal" });
}

/** Synthetic Primary manifest for either archetype (fallback when no DB package). */
export function defaultAlternateForArchetype(args: {
  formatKey: OpportunityFormatKey;
  productType: string;
  archetype: OpportunityDocumentArchetype;
}): ProposalAlternate {
  const contract = args.archetype === "contract";
  const pages = contract ? DEFAULT_CONTRACT_ALTERNATE_PAGES : DEFAULT_PROPOSAL_ALTERNATE_PAGES;
  return {
    id: "default",
    organization_id: "",
    format_key: args.formatKey,
    product_type: args.productType,
    alternate_key: DEFAULT_PROPOSAL_ALTERNATE_KEY,
    label: "Primary",
    description: null,
    sort_order: 0,
    is_default: true,
    archetype: args.archetype,
    pages: pages.map((p) => ({ ...p })),
    created_at: "",
    updated_at: "",
  };
}

export function sortAlternatePages(pages: readonly ProposalAlternatePage[]): ProposalAlternatePage[] {
  return [...pages].sort((a, b) => a.sort_order - b.sort_order || a.page_kind.localeCompare(b.page_kind));
}

export function isValidProposalAlternateKey(key: string): boolean {
  return /^[a-z][a-z0-9_]{0,31}$/.test(key.trim());
}

export function isValidProposalPageKind(value: string): value is ProposalPageKind {
  return (
    (PROPOSAL_BUILTIN_PAGE_KINDS as readonly string[]).includes(value) ||
    value === PROPOSAL_DOC_REF_PAGE_KIND
  );
}

export function isBuiltinProposalPageKind(value: string): value is ProposalBuiltinPageKind {
  return (PROPOSAL_BUILTIN_PAGE_KINDS as readonly string[]).includes(value);
}
